import { autoinject } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { Toaster } from 'app/shared/lib/toaster';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { apiCall } from 'app/shared/lib/essette-api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { EventAggregator } from 'aurelia-event-aggregator';
import { LevelOfCareRevenueCodeMappingReloadEvent } from 'app/um/lib/events';
import { AdmissionCareDayService } from 'app/um/services/admission-care-day-service';
import * as UM from 'app/um/models/essette-api';
import { validate } from 'app/shared/elements/ess-rule';
import { SaveLevelOfCare } from 'app/um/components/levels-of-care/save-level-of-care';
import { IPopup } from 'app/shared/elements/popup-base';
import { deepClone } from 'app/shared/lib/utilities';

@autoinject()
export class LevelsOfCare extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected levelsOfCare: ReadonlyArray<UM.LevelOfCare> = [];
    protected editedLevelOfCare: UM.LevelOfCare | null = null;

    private sorting = new Sorting();
    protected resultCount = 0;
    protected pageSize = 25;
    protected includeInactive = false;

    protected validate = validate;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    protected $saveLevelOfCareModal!: IPopup;
    protected $saveLevelOfCareForm!: IForm;
    protected $saveLevelOfCare!: SaveLevelOfCare;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AdmissionCareDayService,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.reload();
    }

    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }

        this.reload();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadLevelsOfCare();
    }

    protected saveLevelOfCare(levelOfCare: UM.LevelOfCare = {}): void {
        this.editedLevelOfCare = deepClone(levelOfCare);
        this.$saveLevelOfCareModal.open();
    }

    protected saveLevelOfCareClose(): void {
        if(this.$saveLevelOfCareForm.reset) this.$saveLevelOfCareForm.reset();
        this.$saveLevelOfCareModal.close();
    }

    protected async saveLevelOfCareFormSuccess(): Promise<void> {
        this.saveLevelOfCareClose();
        await this.reload();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadLevelsOfCare(): Promise<void> {
        const includes: string[] = [];

        const options: Partial<IQueryLoadOptions> = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: this.includeInactive
        };

        try {
            this.state.beginLoad();

            const response = await this.service.loadLevelsOfCare(options, includes);
            this.levelsOfCare = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateLevelOfCare(levelOfCare: UM.LevelOfCare): Promise<void> {
        if(!levelOfCare.id) return;

        // Show confirmation dialog only if deactivating
        if(levelOfCare.isActive) {
            const result = await confirmation.showDeactivate('um:auth.levelOfCare.$', 1, false);
            if(result.dialogResponse !== confirmation.options.OK) { return; }
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateLevelOfCare(levelOfCare.id, !levelOfCare.isActive);
            isSuccessful = response.isSuccessful;

            this.events.publish(new LevelOfCareRevenueCodeMappingReloadEvent());
            await this.reload();
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.activate('um:auth.levelOfCare.$', !levelOfCare.isActive, isSuccessful);
            this.state.endSave();
        }
    }
}
